import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useEffect } from 'react';

import { appendNewUserFlag } from '#modules/user/features/onboarding/utils/appendNewUserFlag';
import { isNewUserFlag } from '#modules/user/features/onboarding/utils/isNewUserFlag';
import { getMeOptions } from '#modules/user/model/me';
import { toaster } from '#features/ui/components/Toaster';
import { envConfig } from '#constants/envConfig';
import { setNativeToken } from './nativeAuth';
import { NATIVE_OAUTH_DEEPLINK_PREFIX } from './nativeOAuth';
import { takeNativeOAuthReturn } from './nativeOAuthReturn';

/**
 * Listens for the OAuth deep-link callback in the native shell. When
 * teamplay-user finishes the system-browser OAuth flow it redirects to
 * `gg.teamplay.twa://oauth-callback?token=…` (or `?error=…`); Android hands that
 * URL to the app here. We store the JWT, close the browser tab, refresh auth and
 * route to the dashboard. No-op / tree-shaken on web.
 *
 * Mount once, high in the tree (inside the router + query providers).
 */
export const useNativeOAuthDeepLink = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const { t } = useLingui();

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    let remove: (() => void) | undefined;
    let active = true;

    (async () => {
      const [{ App }, { Browser }] = await Promise.all([
        import('@capacitor/app'),
        import('@capacitor/browser'),
      ]);

      const handle = await App.addListener('appUrlOpen', async ({ url }) => {
        if (!url || !url.startsWith(NATIVE_OAUTH_DEEPLINK_PREFIX)) {
          return;
        }

        let token: string | null = null;
        let error: string | null = null;
        let newUser: string | null = null;
        try {
          const parsed = new URL(url);
          token = parsed.searchParams.get('token');
          error = parsed.searchParams.get('error');
          newUser = parsed.searchParams.get('newUser');
        } catch {
          // malformed deep link — ignore
        }

        try {
          await Browser.close();
        } catch {
          // tab may already be closed
        }

        // Привязка, а не вход: приложение уже авторизовано, токен сессии в возврате не приходит
        // и не должен — он осел бы в истории системного браузера. Приходит имя провайдера либо
        // причина отказа, и причины разделены намеренно: человек мог десять минут восстанавливать
        // пароль у провайдера, и «истёк срок» обязано отличаться от «что-то пошло не так».
        const linked = new URL(url).searchParams.get('linked');
        if (linked) {
          await queryClient.invalidateQueries({ queryKey: getMeOptions().queryKey });
          toaster.create({
            title: t`Account linked`,
            type: 'success',
            duration: 5000,
            id: 'oauth-link-result',
          });
          return;
        }

        if (error === 'link.expired' || error === 'link.invalid') {
          toaster.create({
            title:
              error === 'link.expired'
                ? t`The linking window expired. Please try again.`
                : t`This linking link is no longer valid. Please try again.`,
            type: 'error',
            duration: 7000,
            id: 'oauth-link-result',
          });
          return;
        }

        if (token) {
          await setNativeToken(token);
          await queryClient.invalidateQueries({ queryKey: getMeOptions().queryKey });
          router.invalidate();

          // Человек уходил в браузер с конкретной страницы — на неё и возвращаем. Главный случай
          // — приглашение по ссылке: `?invite=<код>` живёт только в адресе, и без возврата место
          // в сборе занять уже нечем, хотя лендинг обещал «подтвердите сразу после входа».
          //
          // Новичок здесь ничем не отличается: он тоже пришёл занять место, а не заполнять
          // профиль. Раньше его уводили на форму профиля, и приглашение терялось — «ссылка ждёт
          // в чате» на деле означало, что сбор начинался без него. Метку новичка везём с собой:
          // попап онбординга висит в layout, всплывает на любой странице, а на самом приглашении
          // откладывает себя до следующего перехода.
          const returnPath = takeNativeOAuthReturn();

          if (returnPath) {
            navigate(
              isNewUserFlag(newUser) ? { href: appendNewUserFlag(returnPath) } : { to: returnPath }
            );
          } else if (isNewUserFlag(newUser)) {
            navigate({
              to: '/{-$locale}/dashboard/settings/profile',
              search: { newUser: 'true' },
            });
          } else {
            navigate({ to: '/{-$locale}/dashboard' });
          }
        } else if (error) {
          toaster.create({ title: error, type: 'error', duration: 5000, id: 'oauth-error-message' });
        }
      });

      if (active) {
        remove = () => handle.remove();
      } else {
        handle.remove();
      }
    })();

    return () => {
      active = false;
      remove?.();
    };
  }, [queryClient, navigate, router, t]);
};
